// Multi-select location filter for the inbox filter bar
// (docs/06-ux-rules.md "Filter bar / Location", FR-53). Presentational —
// `locationIds` and the change callback are owned by the container
// (`FeedbackInbox/index.tsx`), mirroring `InboxFilterBar`'s own contract.
//
// Visuals match the Sketch v83 prototype (order-feedback-landing-page/v2:
// `.f-dd2` trigger, `#mLocD.f-menu.f-menu-loc` panel). Every value below was
// read from the prototype's own CSS and diffed against the rendered result.
// RecoveryAI shows in this menu only when it is on at some locations but not
// all (v83 `#mLocD.rai-on`, the pilot setup): an "N of M on" count beside
// "All locations", "RecoveryAI: On"/"Off" quick-selects, and a status badge
// on each location. Otherwise v83 has "no RAI mention in the location
// dropdown".
//
// A11y note (deviation from the literal 11-chassis-plan.md PR 03 spec, which
// describes `role="listbox" aria-multiselectable` + roving focus): a
// listbox/option pattern is for arbitrary selectable items, not real form
// controls — nesting checkboxes inside `role="option"` produces doubly
// announced ARIA. Each row is a native checkbox in its own <label> inside a
// `role="group"` (the prototype's own markup), keyboard-accessible via native
// Tab + Space. Closing (Escape / outside click) is native `Popover`
// behavior. "All locations" and the RecoveryAI quick-selects also close the
// menu (v83 `closeMenus()`); a location checkbox keeps it open so several
// can be picked. Focus is managed in `useLocationFilter`: opening moves it into
// the search field, and closing returns it to the trigger if it was lost.
import { Button } from '@thanx/uikit/button'
import { Popover } from '@thanx/uikit/popover'
import type { InboxLocation } from 'api/feedbackInbox.types'
import CountBadge from 'components/CountBadge'
import ChevronDownOutline from 'components/Icons/ChevronDownOutline'
import Search from 'components/Icons/Search'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { useLocationFilter } from './useLocationFilter'

const t = buildTranslate('feedback_inbox.filter_bar.location')

const PANEL_MIN_WIDTH_PX = 320
// v83 `#mLocD`: grows for long location names, never past the viewport.
const PANEL_MAX_WIDTH = 'min(360px, 92vw)'
const PANEL_MAX_HEIGHT_PX = 340
const LIST_MAX_HEIGHT_PX = 240
const POPOVER_GAP_PX = 6
const SEARCH_ICON_SIZE_PX = 14
const CHEVRON_SIZE_PX = 13
// v83 `.f-menu` shadow — rgb(30, 38, 48) is the prototype's own shadow ink,
// not a palette token.
const PANEL_SHADOW = '0 8px 24px rgba(30, 38, 48, 0.16)'
// v83 menu rows inherit the prototype body's 1.5 line-height (13px x 1.5).
const MENU_ROW_LINE_HEIGHT = '19.5px'
const PILL_LINE_HEIGHT = '15px'

// 2px belize ring for keyboard focus (WCAG 2.4.7).
const FOCUS_RING_SHADOW = `0 0 0 2px ${palette.belizeHole}`

// baseui drives its own style fn (and focus ring) off `$isFocusVisible`, not
// a `:focus-visible` CSS pseudo-class — see baseui/button/styled-components.js.
type BaseButtonStyleProps = { $isFocusVisible: boolean }

function allCorners(radius: string) {
  return {
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
    borderBottomRightRadius: radius,
    borderBottomLeftRadius: radius,
  }
}

function allSides(width: string, style: 'solid' | 'none', color: string) {
  return {
    borderTopWidth: width,
    borderRightWidth: width,
    borderBottomWidth: width,
    borderLeftWidth: width,
    borderTopStyle: style,
    borderRightStyle: style,
    borderBottomStyle: style,
    borderLeftStyle: style,
    borderTopColor: color,
    borderRightColor: color,
    borderBottomColor: color,
    borderLeftColor: color,
  }
}

// v83 `.f-mi` / `.f-menu-loc .f-mi`.
const menuRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '12px',
  paddingTop: '7px',
  paddingRight: '9px',
  paddingBottom: '7px',
  paddingLeft: '9px',
  ...allCorners('6px'),
  fontSize: '13px',
  lineHeight: MENU_ROW_LINE_HEIGHT,
  cursor: 'pointer',
}

const menuRowHover = {
  backgroundColor: palette.belizeHole10,
  color: palette.belizeHole,
}

// Both uikit wrappers below (Button, Popover) spread a consumer's overrides
// over their own at the SLOT level, so each style fn here replaces uikit's
// for that slot — but baseui then MERGES the result onto its own base style
// for the slot rather than replacing it. Every base property that would
// otherwise leak through (baseui's default primary-kind fills, popover
// shadow/radius, etc.) is therefore re-declared explicitly. Exported because
// styletron's css() is a no-op in jsdom: the style objects are the only
// thing a unit test can assert on. `id` goes through `props` because uikit's
// Button type doesn't accept one; `useLocationFilter` focuses the trigger by
// it on close.
export function triggerButtonOverrides(id: string) {
  return {
    BaseButton: {
      props: { id },
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: '13px',
        fontWeight: 700,
        lineHeight: 'normal',
        color: palette.grey90,
        backgroundColor: palette.white,
        ...allSides('1px', 'solid', palette.grey20),
        ...allCorners('8px'),
        paddingTop: '8px',
        paddingRight: '12px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        height: 'auto',
        width: 'auto',
        whiteSpace: 'nowrap' as const,
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        // v83 `.f-dd2` has no hover/pressed fill.
        ':hover': { backgroundColor: palette.white },
        ':active': { backgroundColor: palette.white },
      }),
    },
  }
}

// A menu row that acts rather than toggles a checkbox: a `Button` styled as
// a v83 `.f-mi` row. `row` is applied last, so it can restore a border side.
function menuRowButtonOverrides(row: {
  fontWeight: number
  color: string
  borderBottomWidth?: string
  borderBottomStyle?: 'solid'
  borderBottomColor?: string
  paddingBottom?: string
  marginBottom?: string
}) {
  return {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        ...menuRow,
        width: '100%',
        height: 'auto',
        textAlign: 'left' as const,
        backgroundColor: 'transparent',
        ...allSides('0px', 'none', 'transparent'),
        ...row,
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        // v83 rows switch on hover instantly; baseui's Button fades its fill.
        transitionProperty: 'none',
        ':hover': menuRowHover,
        ':active': menuRowHover,
      }),
    },
  }
}

// `selectedIds: []` means "all locations" (see LocationFilterProps), so this
// row resets the selection. v83 `.f-mi-clear`.
export const allLocationsButtonOverrides = menuRowButtonOverrides({
  fontWeight: 800,
  color: palette.belizeHole,
})

// v83 `.f-mi.rloc-quick`.
export const raiOnQuickSelectButtonOverrides = menuRowButtonOverrides({
  fontWeight: 400,
  color: palette.grey90,
})

// v83 `.f-mi-clear-last` also rules the last quick-select off from the list.
export const raiOffQuickSelectButtonOverrides = menuRowButtonOverrides({
  fontWeight: 400,
  color: palette.grey90,
  borderBottomWidth: '1px',
  borderBottomStyle: 'solid',
  borderBottomColor: palette.grey10,
  paddingBottom: '9px',
  marginBottom: '4px',
})

// v83 `.f-menu.f-menu-loc`: the Body is the visible panel; Inner only pads.
// Replacing uikit's Body style also drops its 1rem trigger-side margin, so
// baseui's `popoverMargin` alone sets the 6px gap below the trigger.
export const popoverOverrides = {
  Body: {
    style: () => ({
      backgroundColor: palette.white,
      ...allSides('1px', 'solid', palette.grey20),
      ...allCorners('10px'),
      boxShadow: PANEL_SHADOW,
      fontSize: '16px',
      lineHeight: '24px',
      minWidth: `${PANEL_MIN_WIDTH_PX}px`,
      maxWidth: PANEL_MAX_WIDTH,
      maxHeight: `${PANEL_MAX_HEIGHT_PX}px`,
      overflowY: 'auto' as const,
    }),
  },
  Inner: {
    style: () => ({
      backgroundColor: 'transparent',
      ...allCorners('0px'),
      color: palette.grey90,
      paddingTop: '0px',
      paddingRight: '6px',
      paddingBottom: '6px',
      paddingLeft: '6px',
    }),
  },
}

export const dropdownStyles = {
  // v83 `.f-loc-search`
  searchRow: {
    position: 'sticky' as const,
    top: '0px',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    paddingTop: '9px',
    paddingRight: '11px',
    paddingBottom: '7px',
    paddingLeft: '11px',
    backgroundColor: palette.white,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid' as const,
    borderBottomColor: palette.grey10,
  },
  // v83 `.f-loc-search svg` / `.f-dd2 svg`: stroked in currentColor.
  icon: {
    display: 'block',
    flexShrink: 0,
    color: palette.grey70,
  },
  // v83 `.f-loc-search input`. `type="search"` keeps it clear of the app's
  // global `input[type="text"]` rule (48px height, 14px font, 1px border),
  // which outranks a styletron class.
  searchInput: {
    width: '100%',
    minWidth: '0px',
    marginTop: '0px',
    marginRight: '0px',
    marginBottom: '0px',
    marginLeft: '0px',
    paddingTop: '0px',
    paddingRight: '0px',
    paddingBottom: '0px',
    paddingLeft: '0px',
    ...allSides('0px', 'none', 'transparent'),
    outlineStyle: 'none' as const,
    appearance: 'none' as const,
    backgroundColor: 'transparent',
    fontFamily: 'inherit',
    fontSize: '13px',
    lineHeight: 'normal',
    color: palette.grey90,
    '::placeholder': { color: palette.grey70, opacity: 1 },
  },
  // v83 `.f-loc-list`
  list: {
    maxHeight: `${LIST_MAX_HEIGHT_PX}px`,
    overflowY: 'auto' as const,
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  // v83 `label.f-mi.f-mi-check`. marginBottom/fontWeight undo Bootstrap's
  // global `label` rule; paddingTop needs `!important` to beat the app's
  // global `label { padding-top: 0 !important }`.
  checkRow: {
    ...menuRow,
    paddingTop: '7px !important',
    marginBottom: '0px',
    fontWeight: 400,
    color: palette.grey90,
    ':hover': menuRowHover,
  },
  // v83 `.f-mi-check input[type="checkbox"]`. `!important` on the margin:
  // the app's global `input[type="checkbox"] { margin: 4px 0 0 }` outranks a
  // styletron class and would push the box 2px off-centre.
  checkbox: {
    accentColor: palette.belizeHole,
    width: '15px',
    height: '15px',
    flexShrink: 0,
    marginTop: '0px !important',
    marginRight: '0px !important',
    marginBottom: '0px !important',
    marginLeft: '0px !important',
  },
  // v83 `.f-menu-loc .f-mi .loc-nm`
  locationName: {
    display: 'block',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '0%',
    textAlign: 'left' as const,
  },
  noMatches: {
    paddingTop: '7px',
    paddingRight: '9px',
    paddingBottom: '7px',
    paddingLeft: '9px',
    fontSize: '13px',
    lineHeight: MENU_ROW_LINE_HEIGHT,
    color: palette.grey70,
  },
  // v83 `.rloc-pill, .rloc-count`: the per-location badge and the
  // "N of M on" count beside "All locations".
  statusPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    flexShrink: 0,
    fontSize: '10px',
    fontWeight: 800,
    // 10px x the prototype body's 1.5. Without it the pill inherits the
    // row's fixed 19.5px.
    lineHeight: PILL_LINE_HEIGHT,
    letterSpacing: '0.03em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    paddingTop: '3px',
    paddingRight: '8px',
    paddingBottom: '3px',
    paddingLeft: '8px',
    ...allCorners('999px'),
  },
  // v83 `.rloc-pill.on`. #0c5f5d is exactly spearmint90 (6.9:1 here).
  statusPillOn: {
    color: palette.spearmint90,
    backgroundColor: palette.spearmint10,
  },
  // v83 `.rloc-pill.off` / `.rloc-count`: #66757f on #eef2f5 is 4.2:1, under
  // AA for 10px text. grey90 on the nearest grey (grey10) is the same swap
  // CountBadge's `neutral` tone makes.
  statusPillNeutral: {
    color: palette.grey90,
    backgroundColor: palette.grey10,
  },
  // v83 `.rloc-pill .dot`
  statusDot: {
    display: 'block',
    width: '6px',
    height: '6px',
    flexShrink: 0,
    ...allCorners('50%'),
  },
  // v83 #0e8a88 is exactly spearmint70.
  statusDotOn: { backgroundColor: palette.spearmint70 },
  // v83 #9aa8b1 is decorative; grey50 is the nearest token.
  statusDotOff: { backgroundColor: palette.grey50 },
}

type StatusPillProps = {
  tone: 'recoveryAi' | 'neutral'
  label: string
  // Replaces `label` for screen readers when the visible text is too terse
  // to stand alone (e.g. "Off" next to a checkbox).
  srLabel?: string
  showDot?: boolean
}

const StatusPill = ({
  tone,
  label,
  srLabel,
  showDot = false,
}: StatusPillProps) => {
  const [css] = useStyletron()
  const isRecoveryAi = tone === 'recoveryAi'

  return (
    <span
      className={css({
        ...dropdownStyles.statusPill,
        ...(isRecoveryAi
          ? dropdownStyles.statusPillOn
          : dropdownStyles.statusPillNeutral),
      })}
    >
      {showDot && (
        <span
          aria-hidden="true"
          className={css({
            ...dropdownStyles.statusDot,
            ...(isRecoveryAi
              ? dropdownStyles.statusDotOn
              : dropdownStyles.statusDotOff),
          })}
        />
      )}
      {srLabel ? (
        <>
          <span aria-hidden="true">{label}</span>
          <span className="sr-only">{srLabel}</span>
        </>
      ) : (
        label
      )}
    </span>
  )
}

export interface LocationFilterProps {
  locations: InboxLocation[]
  // Empty = all locations (docs/06-ux-rules.md "Location").
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

const LocationFilter = ({
  locations,
  selectedIds,
  onChange,
}: LocationFilterProps) => {
  const [css] = useStyletron()

  const {
    search,
    setSearch,
    isOpen,
    setIsOpen,
    selectedIdSet,
    filteredLocations,
    toggleLocation,
    selectAllLocations,
    showRaiStatus,
    raiOnCount,
    selectRaiOn,
    selectRaiOff,
    triggerAriaLabel,
    searchInputRef,
    triggerId,
    panelRef,
    handleClose,
  } = useLocationFilter(locations, selectedIds, onChange)

  const raiCount = { on: raiOnCount, total: locations.length }

  return (
    <Popover
      placement="bottomRight"
      showArrow={false}
      popoverMargin={POPOVER_GAP_PX}
      overrides={popoverOverrides}
      dismissOnEsc
      dismissOnClickOutside
      onOpen={() => setIsOpen(true)}
      onClose={handleClose}
      content={({ close }) => (
        <div ref={panelRef}>
          <div className={css(dropdownStyles.searchRow)}>
            <Search
              size={SEARCH_ICON_SIZE_PX}
              className={css(dropdownStyles.icon)}
            />
            <input
              ref={searchInputRef}
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('search_placeholder')}
              aria-label={t('search_placeholder')}
              autoComplete="off"
              className={css(dropdownStyles.searchInput)}
            />
          </div>
          <div className={css(dropdownStyles.list)}>
            <Button
              kind="minimal"
              overrides={allLocationsButtonOverrides}
              onClick={() => {
                selectAllLocations()
                close()
              }}
            >
              <span className={css(dropdownStyles.locationName)}>
                {t('all_locations')}
              </span>
              {showRaiStatus && (
                <>
                  {' '}
                  <StatusPill
                    tone="neutral"
                    label={t('rai_count', raiCount)}
                    srLabel={t('rai_count_sr', raiCount)}
                  />
                </>
              )}
            </Button>
            {showRaiStatus && (
              <>
                <Button
                  kind="minimal"
                  overrides={raiOnQuickSelectButtonOverrides}
                  onClick={() => {
                    selectRaiOn()
                    close()
                  }}
                >
                  {t('rai_on_quick_select')}
                </Button>
                <Button
                  kind="minimal"
                  overrides={raiOffQuickSelectButtonOverrides}
                  onClick={() => {
                    selectRaiOff()
                    close()
                  }}
                >
                  {t('rai_off_quick_select')}
                </Button>
              </>
            )}
            <div role="group" aria-label={t('list_aria')}>
              {filteredLocations.length === 0 ? (
                <div className={css(dropdownStyles.noMatches)}>
                  {t('no_matches')}
                </div>
              ) : (
                filteredLocations.map(location => (
                  <label
                    key={location.id}
                    className={css(dropdownStyles.checkRow)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIdSet.has(location.id)}
                      onChange={() => toggleLocation(location.id)}
                      className={css(dropdownStyles.checkbox)}
                    />
                    <span className={css(dropdownStyles.locationName)}>
                      {location.name}
                    </span>
                    {showRaiStatus && location.recoveryAiEnabled !== null && (
                      <>
                        {' '}
                        {location.recoveryAiEnabled ? (
                          <StatusPill
                            tone="recoveryAi"
                            label={t('rai_on_pill')}
                            showDot
                          />
                        ) : (
                          <StatusPill
                            tone="neutral"
                            label={t('rai_off_pill')}
                            srLabel={t('rai_off_pill_sr')}
                            showDot
                          />
                        )}
                      </>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    >
      {/* aria-haspopup="dialog": the popover content is a compound widget
          (a search input, "All locations" and RecoveryAI quick-select
          actions, and a checkbox group), not a single list of selectable
          options. */}
      <Button
        overrides={triggerButtonOverrides(triggerId)}
        aria-label={triggerAriaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {t('trigger')}
        {selectedIds.length > 0 && (
          <CountBadge
            count={selectedIds.length}
            tone="primary"
            size="pill"
            srLabel={triggerAriaLabel}
          />
        )}
        <ChevronDownOutline
          size={CHEVRON_SIZE_PX}
          className={css(dropdownStyles.icon)}
        />
      </Button>
    </Popover>
  )
}

export default LocationFilter
